import {
  CLOUD_LEVELS,
  CURRENCY,
  FINDS,
  SHIP_CLASSES,
  type CloudLevelId,
  type TechId,
} from '@sd/content';
import {
  PIRATES,
  capitalHex,
  cloudAt,
  outpostLimit,
  spaceportLevel,
  type FindCard,
  type FindOffer,
  hexRing,
  isExplored,
  hexDistance,
  type GameState,
  type Hex,
} from '@sd/sim';
import type { Bot } from './match';
import { consumptionPerCycle } from './planner';

/** What a bot reckons a technology fragment and a point of science are worth, ГС. */
const FRAGMENT_WORTH = 60;
const SCIENCE_WORTH = 2;
/** An asteroid site with a free extraction slot to use it; without one it is only a promise. */
const ASTEROID_WORTH = 250;
const ASTEROID_LATER = 40;

/** The card of a find offer a bot takes (#105): the one worth most at today's prices. */
export function pickCard(state: GameState, playerId: string, offer: FindOffer): number {
  const me = state.players.find((p) => p.id === playerId);
  const price = (g: keyof typeof state.market) => state.market[g].price;
  const freeSlot =
    !!me &&
    state.outposts.filter((o) => o.owner === playerId).length <
      outpostLimit(spaceportLevel(me.planet));
  const worth = (c: FindCard): number => {
    switch (c.find) {
      case 'cache': {
        const loot = FINDS.cache.find((x) => x.good === c.good) ?? FINDS.cache[0]!;
        return loot.amount * offer.level * price(loot.good);
      }
      case 'derelict':
        return (
          FINDS.derelict.metals * offer.level * price('metals') +
          FINDS.derelict.fragments * FRAGMENT_WORTH
        );
      case 'anomaly':
        return FINDS.anomaly.science * offer.level * SCIENCE_WORTH;
      case 'asteroid':
        return freeSlot ? ASTEROID_WORTH : ASTEROID_LATER;
    }
  };
  let best = 0;
  offer.cards.forEach((c, i) => {
    if (worth(c) > worth(offer.cards[best]!)) best = i;
  });
  return best;
}

/** ГС the scout always leaves untouched when buying its explorer... */
const CUSHION = 300;
/** ...plus this many cycles of the food and electronics the capital has to import. */
const IMPORT_CYCLES = 10;

/**
 * Clouds this player can scan now: open levels with finds left, nearest
 * first by hex distance (cheap; the move order finds the real path).
 */
function targets(state: GameState, from: Hex, techs: readonly TechId[]) {
  return state.system.clouds
    .filter((c) => canScan(c.level, techs) && (state.exploration.findsLeft[c.id] ?? 0) > 0)
    .filter((c) => !dangerNear(state, c.center))
    .map((cloud) => ({ cloud, d: hexDistance(from, cloud.center) }))
    .sort((a, b) => a.d - b.d || a.cloud.id.localeCompare(b.cloud.id));
}

/** Whether a cloud level is open to a player with these technologies (scanners, #27). */
function canScan(level: CloudLevelId, techs: readonly TechId[]): boolean {
  const tech = CLOUD_LEVELS[level].requiresTech;
  return tech === null || techs.includes(tech);
}

/** Pirates within two hexes, or a band announced for this spot. */
function dangerNear(state: GameState, h: Hex): boolean {
  return (
    state.fleets.some((f) => f.owner === PIRATES && hexDistance(f.hex, h) <= 2) ||
    state.pirateWarnings.some((w) => hexDistance(w.hex, h) <= 2)
  );
}

/**
 * The nearest hex the player has never seen (#100), ring by ring from `from`;
 * none near pirates. Null once the whole map is known.
 */
export function frontier(state: GameState, playerId: string, from: Hex): Hex | null {
  // The same chart from the same hex gives the same answer: worked out once (#50).
  const bits = state.explored[playerId];
  if (!bits) return search(state, playerId, from);
  let known = frontiers.get(bits);
  if (!known) frontiers.set(bits, (known = new Map()));
  const key = `${playerId}:${from.q},${from.r}`;
  if (!known.has(key)) known.set(key, search(state, playerId, from));
  return known.get(key)!;
}

const frontiers = new WeakMap<readonly number[], Map<string, Hex | null>>();

function search(state: GameState, playerId: string, from: Hex): Hex | null {
  const radius = state.system.mapRadius;
  const star = new Set(state.system.starHexes.map((h) => `${h.q},${h.r}`));
  for (let k = 1; k <= radius * 2; k++) {
    for (const h of hexRing(from, k)) {
      if (Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r)) > radius) continue;
      if (star.has(`${h.q},${h.r}`) || isExplored(state, playerId, h) || dangerNear(state, h))
        continue;
      return h;
    }
  }
  return null;
}

/**
 * Phase 2 scout (GDD §7): keeps one explorer, flies it to the nearest dust
 * cloud that still holds finds, scans it dry, moves on; with no known cloud
 * left it explores the map (#100), and goes home once all of it is known.
 */
export const scoutBot: Bot = (state, playerId, rng) => {
  const pick = findPickerBot(state, playerId, rng);
  return pick.length > 0 ? pick : scoutOnly(state, playerId, rng);
};

/** A find on offer (#105) is a card on the screen: picked as soon as it is seen. */
export const findPickerBot: Bot = (state, playerId) => {
  const offer = state.pause ? undefined : state.findOffers.find((o) => o.playerId === playerId);
  return offer
    ? [{ type: 'pickFind', playerId, offerId: offer.id, card: pickCard(state, playerId, offer) }]
    : [];
};

/** The explorer's rounds, without the find cards (#148: those are a pop-up). */
export const scoutOnly: Bot = (state, playerId, rng) => {
  if (state.pause || !rng.chance(0.05)) return [];
  const me = state.players.find((p) => p.id === playerId);
  if (!me) return [];
  const explorer = state.fleets.find(
    (f) => f.owner === playerId && f.ships.some((s) => s.cls === 'explorer'),
  );

  if (!explorer) {
    if (me.shipyard.some((o) => o.ship === 'explorer')) return [];
    const home = capitalHex(state, playerId);
    // An explorer for clouds to scan, or for a map still to explore (#100).
    if (targets(state, home, me.techs).length === 0 && !frontier(state, playerId, home)) return [];
    const cost = SHIP_CLASSES.explorer.cost;
    const gsCash = (me.cash * me.currency.rate) / (1 + CURRENCY.conversionFee);
    // Never buy a ship with the money the capital needs for its imports.
    const use = consumptionPerCycle(me);
    const made = me.planet.lastCycle?.produced;
    const imports = (['food', 'electronics'] as const).reduce(
      (sum, g) => sum + Math.max(0, use[g] - (made?.[g] ?? 0)) * state.market[g].price,
      0,
    );
    if (gsCash < cost.cash + CUSHION + imports * IMPORT_CYCLES) return [];
    if (me.goods.alloys < cost.alloys) return [];
    const fed = me.planet.lastCycle?.fed;
    if (fed && (fed.food < 1 || fed.electronics < 1)) return [];
    if (me.goods.food < use.food * 3 || me.goods.electronics < use.electronics * 3) return [];
    return [{ type: 'buildShip', playerId, ship: 'explorer' }];
  }
  // Pirates announced or close by: run home first (GDD §12: the warning is the chance to leave).
  if (dangerNear(state, explorer.hex) && !explorer.retreating) {
    const home = capitalHex(state, playerId);
    const there = explorer.hex.q === home.q && explorer.hex.r === home.r;
    const heading = explorer.path.at(-1);
    if (!there && !(heading && heading.q === home.q && heading.r === home.r)) {
      return [{ type: 'moveFleet', playerId, fleetId: explorer.id, to: home }];
    }
  }
  if (explorer.path.length > 0 || explorer.scan) return [];

  const here = cloudAt(state.system, explorer.hex);
  if (here && canScan(here.level, me.techs) && (state.exploration.findsLeft[here.id] ?? 0) > 0) {
    return [{ type: 'scan', playerId, fleetId: explorer.id }];
  }
  const next = targets(state, explorer.hex, me.techs)[0];
  if (next) return [{ type: 'moveFleet', playerId, fleetId: explorer.id, to: next.cloud.center }];
  // Nothing known left to scan: go and look at the map (#100).
  const unknown = frontier(state, playerId, explorer.hex);
  if (unknown) return [{ type: 'moveFleet', playerId, fleetId: explorer.id, to: unknown }];
  const home = capitalHex(state, playerId);
  return explorer.hex.q === home.q && explorer.hex.r === home.r
    ? []
    : [{ type: 'moveFleet', playerId, fleetId: explorer.id, to: home }];
};
